import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { useEffect, useMemo, useState } from "react";

import { ArcaneSDK } from "../ArcaneSDK";
import type { Network } from "../config/constants";
import type { Wallet } from "../config/runtime";

export type UseArcaneSdkOptions = {
  rangeApiKey: string;
  /** Target Solana cluster. */
  network: Network;
  /** Override circuit artifact base path/URL (see {@link ArcaneSdkInitOptions.circuitPathsBase}). */
  circuitPathsBase?: string;
};

/**
 * React hook that builds an {@link ArcaneSDK} from wallet-adapter context.
 *
 * Requires `ConnectionProvider` and `WalletProvider` ancestors.
 *
 * @example
 * ```tsx
 * import { useArcaneSdk } from "arcane-privacy-sdk/react";
 *
 * function MyComponent() {
 *   const sdk = useArcaneSdk({ rangeApiKey: import.meta.env.VITE_RANGE_API_KEY, network: "mainnet" });
 *   if (!sdk) return null;
 *   // ...
 * }
 * ```
 */
export function useArcaneSdk(options: UseArcaneSdkOptions): ArcaneSDK | null {
  const { rangeApiKey, network, circuitPathsBase } = options;
  const { connection } = useConnection();
  const wallet = useWallet();
  
  const [sdk, setSdk] = useState<ArcaneSDK | null>(null);

  useEffect(() => {
    if (!wallet) {
      setSdk(null);
      return;
    }

    let cancelled = false;

    void ArcaneSDK.create({
      rangeApiKey,
      network,
      connection,
      wallet: wallet as Wallet,
      circuitPathsBase,
    }).then((instance) => {
      if (!cancelled) {
        setSdk(instance);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [connection, wallet, rangeApiKey, network, circuitPathsBase]);

  return sdk;
}
